"use client"

import * as React from 'react'
import { z } from 'zod'
import { CrudForm, type CrudField, type CrudFormGroup } from '@open-mercato/ui/backend/CrudForm'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@open-mercato/ui/primitives/dialog'
import { Button } from '@open-mercato/ui/primitives/button'
import { useT } from '@open-mercato/shared/lib/i18n/context'
import { createCrudFormError } from '@open-mercato/ui/backend/utils/serverErrors'
import {
  EURO_CLASSES,
  FLEET_CAPABILITIES,
  FUEL_TYPES,
  GPS_PROVIDERS,
  VEHICLE_TYPES,
  shouldVehicleRequireGpsDevice,
  type FleetCapability,
  type VehicleEuroClass,
  type VehicleFuelType,
  type VehicleGpsProvider,
  type VehicleType,
} from './capabilities'
import {
  getEuroClassOptions,
  getFuelTypeOptions,
  getGpsProviderOptions,
  getVehicleTypeOptions,
  type FleetTranslate,
  type FleetVehicleDetail,
} from './fleetTable'

const vehicleDialogSchema = z
  .object({
    registrationPlate: z.string().trim().min(1, 'fleet.validation.registrationPlateRequired'),
    type: z.enum(VEHICLE_TYPES),
    capabilities: z.array(z.enum(FLEET_CAPABILITIES)).min(1, 'fleet.validation.capabilityRequired'),
    brand: z.string().trim().optional().nullable(),
    model: z.string().trim().optional().nullable(),
    year: z.coerce.number().int().min(1980).max(new Date().getUTCFullYear() + 1).optional().nullable(),
    tareWeightKg: z.coerce.number().positive().optional().nullable(),
    maxPayloadKg: z.coerce.number().positive().optional().nullable(),
    euroClass: z.enum(EURO_CLASSES).optional().nullable(),
    fuelType: z.enum(FUEL_TYPES).optional().nullable(),
    insuranceExpires: z.string().trim().optional().nullable(),
    inspectionExpires: z.string().trim().optional().nullable(),
    gpsProvider: z.enum(GPS_PROVIDERS).optional().nullable(),
    gpsDeviceId: z.string().trim().optional().nullable(),
    isActive: z.boolean().optional().default(true),
  })
  .superRefine((value, ctx) => {
    if (new Set(value.capabilities).size !== value.capabilities.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['capabilities'],
        message: 'fleet.validation.capabilitiesUnique',
      })
    }
    if (value.gpsProvider && shouldVehicleRequireGpsDevice(value.gpsProvider) && !value.gpsDeviceId?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['gpsDeviceId'],
        message: 'fleet.validation.gpsDeviceRequired',
      })
    }
  })

export type FleetVehicleFormValues = z.infer<typeof vehicleDialogSchema>

export type FleetVehicleMutationPayload = {
  registrationPlate: string
  type: VehicleType
  capabilities: FleetCapability[]
  brand?: string | null
  model?: string | null
  year?: number | null
  tareWeightKg?: number | null
  maxPayloadKg?: number | null
  euroClass?: VehicleEuroClass | null
  fuelType?: VehicleFuelType | null
  insuranceExpires?: string | null
  inspectionExpires?: string | null
  gpsProvider?: VehicleGpsProvider | null
  gpsDeviceId?: string | null
  isActive?: boolean
}

function normalizeOptionalText(value: unknown): string | null | undefined {
  if (value == null) return undefined
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : null
}

function normalizeOptionalNumber(value: unknown): number | null | undefined {
  if (value == null || value === '') return undefined
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : null
  }
  return null
}

function normalizeOptionalDate(value: unknown): string | null | undefined {
  if (value == null) return undefined
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : null
}

export function toFleetVehicleMutationPayload(values: FleetVehicleFormValues): FleetVehicleMutationPayload {
  const payload: FleetVehicleMutationPayload = {
    registrationPlate: values.registrationPlate.trim(),
    type: values.type,
    capabilities: values.capabilities,
    isActive: values.isActive ?? true,
  }
  const brand = normalizeOptionalText(values.brand)
  const model = normalizeOptionalText(values.model)
  const year = normalizeOptionalNumber(values.year)
  const tareWeightKg = normalizeOptionalNumber(values.tareWeightKg)
  const maxPayloadKg = normalizeOptionalNumber(values.maxPayloadKg)
  const insuranceExpires = normalizeOptionalDate(values.insuranceExpires)
  const inspectionExpires = normalizeOptionalDate(values.inspectionExpires)
  const gpsDeviceId = normalizeOptionalText(values.gpsDeviceId)

  if (brand !== undefined) payload.brand = brand
  if (model !== undefined) payload.model = model
  if (year !== undefined) payload.year = year
  if (tareWeightKg !== undefined) payload.tareWeightKg = tareWeightKg
  if (maxPayloadKg !== undefined) payload.maxPayloadKg = maxPayloadKg
  if (values.euroClass !== undefined) payload.euroClass = values.euroClass ?? null
  if (values.fuelType !== undefined) payload.fuelType = values.fuelType ?? null
  if (insuranceExpires !== undefined) payload.insuranceExpires = insuranceExpires
  if (inspectionExpires !== undefined) payload.inspectionExpires = inspectionExpires
  if (values.gpsProvider !== undefined) payload.gpsProvider = values.gpsProvider ?? null
  if (gpsDeviceId !== undefined) payload.gpsDeviceId = gpsDeviceId

  return payload
}

export function toFleetVehicleFormValues(vehicle?: Partial<FleetVehicleDetail> | null): FleetVehicleFormValues {
  return {
    registrationPlate: vehicle?.registrationPlate ?? '',
    type: vehicle?.type ?? 'TRUCK',
    capabilities: vehicle?.capabilities ?? [],
    brand: vehicle?.brand ?? null,
    model: vehicle?.model ?? null,
    year: vehicle?.year ?? null,
    tareWeightKg: vehicle?.tareWeightKg ?? null,
    maxPayloadKg: vehicle?.maxPayloadKg ?? null,
    euroClass: vehicle?.euroClass ?? null,
    fuelType: vehicle?.fuelType ?? null,
    insuranceExpires: vehicle?.insuranceExpires ?? null,
    inspectionExpires: vehicle?.inspectionExpires ?? null,
    gpsProvider: vehicle?.gpsProvider ?? null,
    gpsDeviceId: vehicle?.gpsDeviceId ?? null,
    isActive: vehicle?.isActive ?? true,
  }
}

export function buildFleetVehicleFields(t: FleetTranslate): CrudField[] {
  return [
    {
      id: 'registrationPlate',
      label: t('fleet.form.fields.registrationPlate', 'Registration plate'),
      type: 'text',
      required: true,
    },
    {
      id: 'type',
      label: t('fleet.form.fields.type', 'Type'),
      type: 'select',
      options: getVehicleTypeOptions(t),
      required: true,
    },
    {
      id: 'capabilities',
      label: t('fleet.form.fields.capabilities', 'Capabilities'),
      type: 'tags',
      suggestions: [...FLEET_CAPABILITIES],
      allowCustomValues: false,
      required: true,
      layout: 'full',
    },
    {
      id: 'brand',
      label: t('fleet.form.fields.brand', 'Brand'),
      type: 'text',
      layout: 'half',
    },
    {
      id: 'model',
      label: t('fleet.form.fields.model', 'Model'),
      type: 'text',
      layout: 'half',
    },
    {
      id: 'year',
      label: t('fleet.form.fields.year', 'Year'),
      type: 'number',
      layout: 'half',
    },
    {
      id: 'tareWeightKg',
      label: t('fleet.form.fields.tareWeightKg', 'Tare weight (kg)'),
      type: 'number',
      layout: 'half',
    },
    {
      id: 'maxPayloadKg',
      label: t('fleet.form.fields.maxPayloadKg', 'Max payload (kg)'),
      type: 'number',
      layout: 'half',
    },
    {
      id: 'euroClass',
      label: t('fleet.form.fields.euroClass', 'Euro class'),
      type: 'select',
      options: getEuroClassOptions(t),
      layout: 'half',
    },
    {
      id: 'fuelType',
      label: t('fleet.form.fields.fuelType', 'Fuel type'),
      type: 'select',
      options: getFuelTypeOptions(t),
      layout: 'half',
    },
    {
      id: 'insuranceExpires',
      label: t('fleet.form.fields.insuranceExpires', 'Insurance expires'),
      type: 'date',
      layout: 'half',
    },
    {
      id: 'inspectionExpires',
      label: t('fleet.form.fields.inspectionExpires', 'Inspection expires'),
      type: 'date',
      layout: 'half',
    },
    {
      id: 'gpsProvider',
      label: t('fleet.form.fields.gpsProvider', 'GPS provider'),
      type: 'select',
      options: getGpsProviderOptions(t),
      layout: 'half',
    },
    {
      id: 'gpsDeviceId',
      label: t('fleet.form.fields.gpsDeviceId', 'GPS device ID'),
      type: 'text',
      layout: 'half',
    },
    {
      id: 'isActive',
      label: t('fleet.form.fields.isActive', 'Active'),
      type: 'checkbox',
      layout: 'half',
    },
  ]
}

export function buildFleetVehicleGroups(t: FleetTranslate): CrudFormGroup[] {
  return [
    {
      id: 'identity',
      title: t('fleet.form.groups.identity', 'Identity'),
      fields: ['registrationPlate', 'type', 'brand', 'model', 'year', 'isActive'],
    },
    {
      id: 'operations',
      title: t('fleet.form.groups.operations', 'Operations'),
      fields: ['capabilities', 'tareWeightKg', 'maxPayloadKg', 'euroClass', 'fuelType'],
    },
    {
      id: 'documents',
      title: t('fleet.form.groups.documents', 'Documents'),
      fields: ['insuranceExpires', 'inspectionExpires'],
    },
    {
      id: 'telematics',
      title: t('fleet.form.groups.telematics', 'Telematics'),
      fields: ['gpsProvider', 'gpsDeviceId'],
    },
  ]
}

export function assertFleetVehicleDialogPayload(
  t: FleetTranslate,
  values: FleetVehicleFormValues,
): FleetVehicleMutationPayload {
  const parsed = vehicleDialogSchema.safeParse(values)
  if (!parsed.success) {
    const fieldErrors: Record<string, string> = {}
    for (const issue of parsed.error.issues) {
      const fieldId = typeof issue.path[0] === 'string' ? issue.path[0] : 'form'
      fieldErrors[fieldId] = t(issue.message, issue.message)
    }
    throw createCrudFormError(
      t('fleet.messages.validationFailed', 'Please fix the highlighted fields.'),
      fieldErrors,
    )
  }
  return toFleetVehicleMutationPayload(parsed.data)
}

export function FleetVehicleDialog({
  open,
  onOpenChange,
  mode,
  initialValues,
  onSubmit,
  loading = false,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  mode: 'create' | 'edit'
  initialValues?: FleetVehicleFormValues
  onSubmit: (payload: FleetVehicleMutationPayload) => Promise<void>
  loading?: boolean
}) {
  const t = useT()
  const fields = React.useMemo(() => buildFleetVehicleFields(t), [t])
  const groups = React.useMemo(() => buildFleetVehicleGroups(t), [t])
  const title = mode === 'create'
    ? t('fleet.dialog.createTitle', 'Add vehicle')
    : t('fleet.dialog.editTitle', 'Edit vehicle')

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-4xl">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <CrudForm<FleetVehicleFormValues>
          key={`${mode}:${initialValues?.registrationPlate ?? 'new'}:${open ? 'open' : 'closed'}`}
          schema={vehicleDialogSchema}
          embedded={true}
          isLoading={loading}
          fields={fields}
          groups={groups}
          initialValues={initialValues}
          submitLabel={mode === 'create'
            ? t('fleet.dialog.submitCreate', 'Create vehicle')
            : t('fleet.dialog.submitEdit', 'Save changes')}
          extraActions={(
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('common.cancel', 'Cancel')}
            </Button>
          )}
          onSubmit={async (values) => {
            await onSubmit(assertFleetVehicleDialogPayload(t, values))
          }}
        />
      </DialogContent>
    </Dialog>
  )
}
